import React, { useCallback, useState } from "react";
import { Modal, Form } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import TextArea from "antd/lib/input/TextArea";
import { IEquipment } from "..";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";

interface ISendMessageModalProps {
  visible: boolean;
  handleCloseModal: () => void;
  user?: IEquipment;
}

interface IFormData {
  message: string;
}

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

const SendMessageModal: React.FC<ISendMessageModalProps> = ({
  visible,
  handleCloseModal,
  user,
}) => {
  const { t } = useTranslation(["users"]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { addToast } = useToast();
  const [form] = Form.useForm();

  const handleClose = useCallback(() => {
    form.resetFields();
    handleCloseModal();
  }, [form, handleCloseModal]);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O usuário não foi encontrado";
          break;
        default:
          description = "Ocorreu um erro ao enviar a mensagem, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro no envio",
        description,
      });
    },
    [addToast]
  );

  const handleSubmit = useCallback(
    async (data: IFormData) => {
      try {
        const schema = Yup.object().shape({
          message: Yup.string().required("Mensagem obrigatória"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { message } = data;

        const payload = {
          message,
        };

        const response: AxiosResponse = await api.post(
          `/notifications/${user?.id}`,
          payload
        );
        addToast({
          type: "success",
          title: "Mensagem enviada",
          description: "A sua mensagem foi enviada com sucesso",
        });

        handleClose();
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [addToast, generateErrorToast, handleClose, user?.id]
  );

  return (
    <Modal
      title={`Enviar para ${user?.equipment_type}`}
      visible={visible}
      onOk={() => form.submit()}
      okText="Enviar"
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      destroyOnClose
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="message"
          required
          label="Mensagem"
          errors={validationErrors}
        >
          <TextArea rows={4} spellCheck={false} />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default SendMessageModal;
